import { useMemo } from 'react';
import {
  RECRUIT_PROFILE_CAP,
  RECRUIT_PROFILE_OPTIONS,
  writeRecruitProfile,
  readRecruitProfile
} from './recruitProfile';
import type { RecruitProfile, RecruitProfileKind } from './recruitProfile';
import { readRecruitIdeaPath, writeRecruitIdeaPath } from './partials/RecruitIdeaPath';
import type { RecruitIdeaPath } from './partials/RecruitIdeaPath';
import { readRecruitIdeaWho, saveRecruitWhoNotes } from './partials/recruitIdeaWho';
import type { RecruitIdeaWho } from './partials/recruitIdeaWho';
import { readClientNotes, reloadClientNotes } from './clientNotesStore';
import { useQuizInvitations } from '../../CoverQuiz/coverQuizStorage';
import { readClientQuizzes, removeQuiz, stampQuizApplied } from '../../../store/clientQuiz/cache';
import type { AdviserQuiz } from '../../../store/clientQuiz/api';
import { partTwoAnswersOf } from '../../CareerQuiz/careerPartTwoData';
import type { StoredPartTwo } from '../../CareerQuiz/careerPartTwoData';

/**
 * A finished 職涯天賦測驗, as the adviser's readers see it: the completed career
 * ClientQuiz row of the candidate's factsheet, read through the shared
 * per-factsheet cache in store/clientQuiz/cache.
 */

export interface RecruitQuizResult {
  clientId: string;
  /** The ClientQuiz row this result came from. */
  id?: string;
  candidateName?: string;
  path: RecruitIdeaPath;
  yearsOfExp: number | null;
  targetIncome: number | null;
  painPoints: string[];
  strengths: string[];
  discLetter: 'D' | 'I' | 'S' | 'C';
  concerns: string[];
  /** 快問快答 picks (option keys, e.g. 'freedom', 'social') — for the report echo. */
  vibes?: string[];
  /** 你想要的生活 — raw option keys from careerDreamData (absent on results from before these questions). */
  idealDay: string[];
  dream: string | null;
  horizon: number | null;
  identity: string | null;
  completedAt: string;
  isApplied?: boolean;
  appliedAt?: string;
  /**
   * 第二部分 was answered after the last 同步至招募檔案. Its 想要的支援 (concerns)
   * only arrive with part 2 (since 2026-10-01), so a sync made after part 1
   * carried none — the row is no longer 「已同步」 until the adviser syncs again.
   */
  partTwoSinceApplied?: boolean;
}

/** Whether a career row holds part-1 answers — not only a 第二部分 answered on its own (`part2`). */
const hasPartOne = (answers: Record<string, unknown>) => Object.keys(answers).some(key => key !== 'part2');

/** Part 2 merged into this row after it was last synced to the profile. */
const partTwoAfter = (row: AdviserQuiz): boolean =>
  Boolean(row.appliedAt && row.partTwoAt && new Date(row.partTwoAt).getTime() > new Date(row.appliedAt).getTime());

/** Maps a completed career ClientQuiz row to the result shape the readers use; exported so
 * RecruitQuizDialog can build it from `useQuizInvitations`'s own `list` without a second,
 * separately-polled subscription. Null when the row holds only 第二部分 (see `careerPartTwoOf`). */
export const careerResultOf = (
  clientId: string | null | undefined,
  row: AdviserQuiz | null | undefined
): RecruitQuizResult | null => {
  if (!clientId || !row || !row.answers || !hasPartOne(row.answers)) return null;
  const a = row.answers as Record<string, any>;
  return {
    clientId,
    id: row.id,
    candidateName: row.displayName || undefined,
    path: a.path === 'graduate' ? 'graduate' : 'experienced',
    yearsOfExp: typeof a.yearsOfExp === 'number' ? a.yearsOfExp : null,
    targetIncome: typeof a.targetIncome === 'number' ? a.targetIncome : null,
    painPoints: Array.isArray(a.painPoints) ? a.painPoints : [],
    strengths: Array.isArray(a.strengths) ? a.strengths : [],
    discLetter: ['D', 'I', 'S', 'C'].includes(a.discLetter) ? a.discLetter : 'S',
    // 想要的支援 is asked in 第二部分 (since 2026-10-01), merged under `part2`;
    // a part-1 answer from before then is still read first.
    concerns: Array.isArray(a.concerns)
      ? a.concerns
      : a.part2 && Array.isArray(a.part2.concerns)
      ? a.part2.concerns.filter((k: unknown) => typeof k === 'string')
      : [],
    vibes: Array.isArray(a.vibes) ? a.vibes : [],
    idealDay: Array.isArray(a.idealDay) ? a.idealDay.filter((k: unknown) => typeof k === 'string') : [],
    dream: typeof a.dream === 'string' ? a.dream : null,
    horizon: typeof a.horizon === 'number' ? a.horizon : null,
    identity: typeof a.identity === 'string' ? a.identity : null,
    completedAt: row.submittedAt || row.createdAt,
    isApplied: Boolean(row.appliedAt) && !partTwoAfter(row),
    appliedAt: row.appliedAt,
    partTwoSinceApplied: partTwoAfter(row)
  };
};

/** 職涯測驗 第二部分 on a completed career row (`answers.part2`), and when it was answered. */
export interface RecruitQuizPartTwo {
  answers: StoredPartTwo;
  completedAt: string;
}

export const careerPartTwoOf = (row: AdviserQuiz | null | undefined): RecruitQuizPartTwo | null => {
  const answers = row ? partTwoAnswersOf(row.answers) : null;
  if (!row || !answers) return null;
  return { answers, completedAt: row.partTwoAt || row.submittedAt || row.createdAt };
};

export const readRecruitQuiz = (clientId?: string | null): RecruitQuizResult | null =>
  careerResultOf(clientId, readClientQuizzes(readClientNotes(clientId)?.factsheetId, 'career')?.completed);

/** One candidate's finished career quiz, re-rendering whenever the cache or the notes change. */
export const useRecruitQuiz = (
  clientId?: string | null,
  options: { pollMs?: number } = {}
): RecruitQuizResult | null => {
  const { list } = useQuizInvitations(clientId, 'career', options);
  return useMemo(() => careerResultOf(clientId, list?.completed), [clientId, list]);
};

export const clearRecruitQuiz = async (clientId: string): Promise<boolean> => {
  const factsheetId = readClientNotes(clientId)?.factsheetId;
  const row = readClientQuizzes(factsheetId, 'career')?.completed;
  if (!factsheetId || !row) return false;
  const cleared = await removeQuiz(factsheetId, 'career', row.id, 'completed');
  // `quizCompletedAt.career` (what the list reads) only a client re-read refreshes.
  if (cleared) reloadClientNotes(clientId);
  return cleared;
};

type DiscLetter = RecruitQuizResult['discLetter'];
const isDisc = (value: unknown): value is DiscLetter =>
  value === 'D' || value === 'I' || value === 'S' || value === 'C';

export type QuizMergeKept = 'path' | 'yearsOfExp' | 'targetIncome' | 'disc';

export interface QuizMergeSummary {
  added: Record<RecruitProfileKind, number>;
  pathSet: boolean;
  yearsSet: boolean;
  incomeSet: boolean;
  discSet: boolean;
  /** Answers that differ from what the recruiter already entered, left as the recruiter had them. */
  kept: QuizMergeKept[];
}

const PROFILE_KINDS: RecruitProfileKind[] = ['painPoints', 'concerns', 'strengths'];

/**
 * 同步至招募檔案 — a safe merge of the candidate's answers into the recruiter's
 * notes (spec §10.1, 🔒 never destroy what the recruiter entered):
 * - 痛點 / 顧慮 / 優勢: the recruiter's picks first, then the quiz's new ones, capped at 3;
 * - 招募路線, 年資, 目標月入: filled only when empty;
 * - DISC: set only when nothing is on file (`currentDisc`);
 * - 現職, 人脈, 介紹人: never touched;
 * - the quiz row is stamped as applied.
 * Returns what changed and what was kept, for `quizMergeMessage`.
 */
export const applyQuizToRecruitProfile = (
  clientId: string,
  quiz: RecruitQuizResult,
  options: {
    currentDisc?: string | null;
    /** Returns false when the change was not allowed (e.g. no edit rights). */
    onChangePersonality?: (letter: DiscLetter) => boolean | void;
  } = {}
): QuizMergeSummary => {
  const summary: QuizMergeSummary = {
    added: { painPoints: 0, concerns: 0, strengths: 0 },
    pathSet: false,
    yearsSet: false,
    incomeSet: false,
    discSet: false,
    kept: []
  };
  if (!clientId || !quiz) return summary;

  // 1. 痛點 / 顧慮 / 優勢 — add, never replace.
  const profile = readRecruitProfile(clientId);
  const merged = { ...profile } as RecruitProfile;
  PROFILE_KINDS.forEach(kind => {
    const fresh = quiz[kind].filter(
      (key, index, all) =>
        RECRUIT_PROFILE_OPTIONS[kind].includes(key) && !profile[kind].includes(key) && all.indexOf(key) === index
    );
    const next = [...profile[kind], ...fresh].slice(0, RECRUIT_PROFILE_CAP);
    summary.added[kind] = next.length - profile[kind].length;
    merged[kind] = next;
  });
  if (PROFILE_KINDS.some(kind => summary.added[kind] > 0)) writeRecruitProfile(clientId, merged);

  // 2. 招募路線 — only when not chosen yet.
  const path = readRecruitIdeaPath(clientId);
  if (!path) {
    writeRecruitIdeaPath(clientId, quiz.path);
    summary.pathSet = true;
  } else if (path !== quiz.path) {
    summary.kept.push('path');
  }

  // 3. 他是誰 — 年資 and 目標月入 each only when empty; the other three untouched.
  const who = readRecruitIdeaWho(clientId) || {
    occupation: '',
    yearsOfExp: null,
    network: '',
    targetIncome: null,
    referrer: ''
  };
  const nextWho: RecruitIdeaWho = { ...who };
  if (quiz.yearsOfExp !== null) {
    if (who.yearsOfExp === null) {
      nextWho.yearsOfExp = quiz.yearsOfExp;
      summary.yearsSet = true;
    } else if (who.yearsOfExp !== quiz.yearsOfExp) {
      summary.kept.push('yearsOfExp');
    }
  }
  if (quiz.targetIncome !== null) {
    if (who.targetIncome === null) {
      nextWho.targetIncome = quiz.targetIncome;
      summary.incomeSet = true;
    } else if (who.targetIncome !== quiz.targetIncome) {
      summary.kept.push('targetIncome');
    }
  }
  // The notes half only: 現職 / 介紹人 are real factsheet fields the sync never rewrites.
  if (summary.yearsSet || summary.incomeSet) {
    saveRecruitWhoNotes(clientId, {
      yearsOfExp: nextWho.yearsOfExp,
      network: nextWho.network || null,
      targetIncome: nextWho.targetIncome
    });
  }

  // 4. DISC — only when nothing is on file.
  if (isDisc(quiz.discLetter)) {
    if (!isDisc(options.currentDisc)) {
      if (options.onChangePersonality) {
        summary.discSet = options.onChangePersonality(quiz.discLetter) !== false;
      }
    } else if (options.currentDisc !== quiz.discLetter) {
      summary.kept.push('disc');
    }
  }

  // 5. Stamp the row as applied.
  const factsheetId = readClientNotes(clientId)?.factsheetId;
  if (quiz.id && factsheetId) void stampQuizApplied(factsheetId, 'career', quiz.id).catch(() => undefined);

  return summary;
};

type MergeCopy = {
  added: Record<RecruitProfileKind, (n: number) => string>;
  field: Record<QuizMergeKept, string>;
  addedLine: (items: string) => string;
  filledLine: (items: string) => string;
  keptLine: (items: string) => string;
  list: string;
  join: string;
  nothing: string;
};

// Hardcoded rather than react-intl keys: the dialog around it hardcodes its
// Traditional Chinese, while the card in 招募 IDEA follows the app's language.
const MERGE_COPY: Record<'zh-Hant-HK' | 'zh' | 'en', MergeCopy> = {
  'zh-Hant-HK': {
    added: { painPoints: n => `${n} 個痛點`, concerns: n => `${n} 個顧慮`, strengths: n => `${n} 個優勢` },
    field: { path: '招募路線', yearsOfExp: '年資', targetIncome: '目標月入', disc: '性格類型' },
    addedLine: items => `已加入 ${items}`,
    filledLine: items => `已填入${items}`,
    keptLine: items => `${items}已有資料，未更改`,
    list: '、',
    join: '；',
    nothing: '沒有新的資料需要加入'
  },
  zh: {
    added: { painPoints: n => `${n} 个痛点`, concerns: n => `${n} 个顾虑`, strengths: n => `${n} 个优势` },
    field: { path: '招募路线', yearsOfExp: '年资', targetIncome: '目标月入', disc: '性格类型' },
    addedLine: items => `已加入 ${items}`,
    filledLine: items => `已填入${items}`,
    keptLine: items => `${items}已有资料，未更改`,
    list: '、',
    join: '；',
    nothing: '没有新的资料需要加入'
  },
  en: {
    added: {
      painPoints: n => `${n} pain point${n === 1 ? '' : 's'}`,
      concerns: n => `${n} concern${n === 1 ? '' : 's'}`,
      strengths: n => `${n} strength${n === 1 ? '' : 's'}`
    },
    field: {
      path: 'career path',
      yearsOfExp: 'years of experience',
      targetIncome: 'target income',
      disc: 'personality type'
    },
    addedLine: items => `Added ${items}`,
    filledLine: items => `Filled in ${items}`,
    keptLine: items => `${items} already set, left unchanged`,
    list: ', ',
    join: '; ',
    nothing: 'Nothing new to add'
  }
};

const mergeCopyFor = (locale?: string): MergeCopy =>
  locale === 'zh' || locale === 'en'
    ? MERGE_COPY[locale]
    : locale?.startsWith('en')
    ? MERGE_COPY.en
    : MERGE_COPY['zh-Hant-HK'];

/** One line for the recruiter, e.g. 「已加入 2 個痛點；年資已有資料，未更改」. Traditional Chinese unless `locale` says otherwise. */
export const quizMergeMessage = (summary: QuizMergeSummary, locale?: string): string => {
  const copy = mergeCopyFor(locale);
  const parts: string[] = [];
  const added = PROFILE_KINDS.filter(kind => summary.added[kind] > 0).map(kind =>
    copy.added[kind](summary.added[kind])
  );
  if (added.length) parts.push(copy.addedLine(added.join(copy.list)));
  const filled = (
    [
      [summary.pathSet, 'path'],
      [summary.yearsSet, 'yearsOfExp'],
      [summary.incomeSet, 'targetIncome'],
      [summary.discSet, 'disc']
    ] as Array<[boolean, QuizMergeKept]>
  )
    .filter(([set]) => set)
    .map(([, field]) => copy.field[field]);
  if (filled.length) parts.push(copy.filledLine(filled.join(copy.list)));
  if (summary.kept.length) parts.push(copy.keptLine(summary.kept.map(field => copy.field[field]).join(copy.list)));
  if (!parts.length) return copy.nothing;
  const line = parts.join(copy.join);
  // English starts a sentence with a capital even when it opens on a field name.
  return line.charAt(0).toUpperCase() + line.slice(1);
};
